// QA override: `?exp_override=<flag_key>:<variant>` forces a variant for the
// session via a cookie. Gated to non-production by default.
//
// Format of the cookie value (URL-encoded JSON):
//   { "<flag_key>": "<variant_value>", ... }
//
// Multiple overrides can stack: append `?exp_override=…` multiple times in the
// URL (each call merges into the cookie).

const COOKIE_NAME = 'opal_exp_override';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24; // 24h — short enough to not leak QA state into long-running prod sessions.

type Document = { cookie: string };

export interface OverrideEnv {
  /** Window location to parse `exp_override` query param from. */
  search: string;
  /** Cookie jar (document.cookie style). */
  document?: Document;
  /**
   * Gate flag for whether overrides are honored. Only `false` blocks;
   * `true` and `undefined` both allow. Callers should pass an explicit
   * boolean computed from environment (see `defaultAllowed`) at call sites
   * to make the gate audit-able.
   */
  allowed?: boolean;
}

/**
 * Parses the active QA overrides for this session. Reads from the cookie and
 * merges in any new value from the URL query param. Persists the merged map
 * back to the cookie so the override survives navigation within the SPA.
 *
 * Returns an empty object when overrides are disallowed.
 */
export function readAndPersistOverrides(env: OverrideEnv): Record<string, string> {
  if (env.allowed === false) return {};
  const existing = readCookie(env.document) ?? {};
  const fromUrl = parseQueryOverride(env.search);
  if (!fromUrl || Object.keys(fromUrl).length === 0) {
    return existing;
  }
  const merged = { ...existing, ...fromUrl };
  writeCookie(merged, env.document);
  return merged;
}

/** Pure read — does not consult the URL. Used by the enrichment plugin and CAPI passthrough. */
export function readOverrides(env: Pick<OverrideEnv, 'document' | 'allowed'>): Record<string, string> {
  if (env.allowed === false) return {};
  return readCookie(env.document) ?? {};
}

export function parseQueryOverride(search: string): Record<string, string> | null {
  if (!search) return null;
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const all = params.getAll('exp_override');
  if (!all.length) return null;
  const out: Record<string, string> = {};
  for (const entry of all) {
    const idx = entry.indexOf(':');
    if (idx <= 0 || idx === entry.length - 1) continue;
    const key = entry.slice(0, idx).trim();
    const value = entry.slice(idx + 1).trim();
    if (!/^[a-z0-9_-]+$/i.test(key)) continue;
    if (!value) continue;
    out[key] = value;
  }
  return out;
}

function readCookie(doc?: Document): Record<string, string> | null {
  const cookie = doc?.cookie;
  if (!cookie) return null;
  const match = cookie.split(/;\s*/).find((c) => c.startsWith(`${COOKIE_NAME}=`));
  if (!match) return null;
  const raw = decodeURIComponent(match.slice(COOKIE_NAME.length + 1));
  try {
    const parsed = JSON.parse(raw) as Record<string, string>;
    if (parsed && typeof parsed === 'object') return parsed;
  } catch {
    /* swallow malformed cookie */
  }
  return null;
}

function writeCookie(value: Record<string, string>, doc?: Document): void {
  if (!doc) return;
  const encoded = encodeURIComponent(JSON.stringify(value));
  doc.cookie = `${COOKIE_NAME}=${encoded}; max-age=${COOKIE_MAX_AGE_SECONDS}; path=/; SameSite=Lax`;
}

/**
 * Convenience for runtime detection — true when overrides should be honored.
 * Vite replaces __ENVIRONMENT__ at build time; missing in tests defaults to 'development'.
 */
export function defaultAllowed(environment: string | undefined): boolean {
  return environment !== 'production';
}
